import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Slider } from './ui/slider.jsx';
import { Settings, Save, RotateCcw, Shield, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';

// Defaults must match backend RightsizingSettings model exactly so a fresh
// cluster behaves identically to one with no settings doc.
const DEFAULT_SETTINGS = {
  protected_namespaces: [
    'kube-system', 'kube-public', 'kube-node-lease', 'optops-system',
    'ingress-nginx', 'cert-manager',
  ],
  excluded_workloads: [],
  buffer_threshold_pct: 0.15,   // 15% on top of percentile baseline
  min_confidence_pct: 70,        // surface recs at ≥ 70% confidence
  rightsize_daemonset: false,    // DaemonSet rightsizing opt-in (default OFF)
};

function RightsizingSettings({ clusterId }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newProtectedNamespace, setNewProtectedNamespace] = useState('');
  const [newExcludedWorkload, setNewExcludedWorkload] = useState('');

  useEffect(() => {
    if (clusterId) {
      fetchSettings();
    }
  }, [clusterId]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/rightsizing/${clusterId}`);
      // Backend tolerates legacy fields and returns only the current shape.
      setSettings({ ...DEFAULT_SETTINGS, ...response.data });
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Keep defaults if fetch fails (e.g. 404 first time)
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/rightsizing/${clusterId}`, settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setSettings(DEFAULT_SETTINGS);
    toast.info('Reset to defaults — click Save to apply');
  };

  const addProtectedNamespace = () => {
    const ns = newProtectedNamespace.trim();
    if (!ns) return;
    if (settings.protected_namespaces.includes(ns)) {
      toast.error(`'${ns}' is already protected`);
      return;
    }
    setSettings({ ...settings, protected_namespaces: [...settings.protected_namespaces, ns] });
    setNewProtectedNamespace('');
  };

  const removeProtectedNamespace = (ns) => {
    setSettings({
      ...settings,
      protected_namespaces: settings.protected_namespaces.filter((n) => n !== ns),
    });
  };

  const addExcludedWorkload = () => {
    const wl = newExcludedWorkload.trim();
    if (!wl) return;
    if (settings.excluded_workloads.includes(wl)) {
      toast.error(`'${wl}' is already excluded`);
      return;
    }
    setSettings({ ...settings, excluded_workloads: [...settings.excluded_workloads, wl] });
    setNewExcludedWorkload('');
  };

  const removeExcludedWorkload = (wl) => {
    setSettings({
      ...settings,
      excluded_workloads: settings.excluded_workloads.filter((w) => w !== wl),
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-slate-500">Loading settings...</CardContent>
      </Card>
    );
  }

  const bufferPctDisplay = Math.round((settings.buffer_threshold_pct ?? 0.15) * 100);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Rightsizing Settings
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleResetToDefaults}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={handleSaveSettings} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* Buffer Threshold */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Buffer Threshold: {bufferPctDisplay}%</Label>
            </div>
            <Slider
              value={[bufferPctDisplay]}
              onValueChange={(v) =>
                setSettings({ ...settings, buffer_threshold_pct: v[0] / 100 })
              }
              min={5}
              max={30}
              step={1}
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-2">
              Safety buffer added on top of the percentile baseline (5–30%). Bursty / batch
              workloads get max(this, 15%) — peak coverage can't be undercut.
            </p>
          </div>

          {/* Min Confidence */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Minimum Confidence: {settings.min_confidence_pct}%</Label>
            </div>
            <Slider
              value={[settings.min_confidence_pct]}
              onValueChange={(v) => setSettings({ ...settings, min_confidence_pct: v[0] })}
              min={50}
              max={95}
              step={5}
              className="mt-2"
            />
            <p className="text-xs text-slate-500 mt-2">
              Recommendations below this confidence are filtered out. Lower = more
              recommendations, noisier signal. Higher = fewer recommendations, higher trust.
            </p>
          </div>

          {/* DaemonSet rightsizing opt-in */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={!!settings.rightsize_daemonset}
                onChange={(e) =>
                  setSettings({ ...settings, rightsize_daemonset: e.target.checked })
                }
              />
              <Label className="cursor-pointer">Rightsize DaemonSets</Label>
            </label>
            <p className="text-xs text-slate-500 mt-2">
              DaemonSets run one pod per node and often carry system/agent workloads, so
              they are excluded by default. When enabled, DaemonSet pods are rightsized
              (container requests/limits only — the per-node replica count is never changed)
              on a slower cadence.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Protected Namespaces */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Protected Namespaces
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
            Namespaces in this list are <strong>never rightsized</strong>. System and
            critical-infra namespaces should stay here.
          </p>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="namespace-name"
              value={newProtectedNamespace}
              onChange={(e) => setNewProtectedNamespace(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addProtectedNamespace())}
            />
            <Button onClick={addProtectedNamespace} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.protected_namespaces.map((ns) => (
              <span
                key={ns}
                className="inline-flex items-center bg-slate-100 dark:bg-slate-800 text-sm rounded px-2 py-1"
              >
                {ns}
                <button
                  onClick={() => removeProtectedNamespace(ns)}
                  className="ml-2 text-slate-400 hover:text-red-500"
                  aria-label={`Remove ${ns}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {settings.protected_namespaces.length === 0 && (
              <span className="text-sm text-slate-400">No namespaces protected</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Excluded Workloads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="w-5 h-5 mr-2" />
            Excluded Workloads
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
            Specific workload names to skip. Supports glob patterns like{' '}
            <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">etcd-*</code>{' '}
            or <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">payments-api</code>.
          </p>
          <div className="flex gap-2 mb-3">
            <Input
              placeholder="workload-name or glob-*"
              value={newExcludedWorkload}
              onChange={(e) => setNewExcludedWorkload(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addExcludedWorkload())}
            />
            <Button onClick={addExcludedWorkload} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.excluded_workloads.map((wl) => (
              <span
                key={wl}
                className="inline-flex items-center bg-slate-100 dark:bg-slate-800 text-sm rounded px-2 py-1"
              >
                {wl}
                <button
                  onClick={() => removeExcludedWorkload(wl)}
                  className="ml-2 text-slate-400 hover:text-red-500"
                  aria-label={`Remove ${wl}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {settings.excluded_workloads.length === 0 && (
              <span className="text-sm text-slate-400">No workloads excluded</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default RightsizingSettings;
